(() => {
  if (customElements.get('norive-sticky-atc-bar')) return;

  class NoriveStickyAtcBar extends HTMLElement {
    constructor() {
      super();

      this.sectionRoot = this.closest('[data-norive-sticky-section]');
      this.sectionHost = this.closest('.shopify-section');
      this.sentinel = this.sectionRoot?.querySelector('[data-sticky-sentinel]');
      this.variantSelect = this.querySelector('[data-sticky-variant-select]');
      this.variantTitle = this.querySelector('[data-sticky-variant-title]');
      this.currentPrice = this.querySelector('[data-sticky-current-price]');
      this.priceWrap = this.querySelector('[data-sticky-price]');
      this.image = this.querySelector('.norive-sticky-atc-bar__image');
      this.submitButton = this.querySelector('[type="submit"]');
      this.buttonText = this.querySelector('[data-sticky-button-text]');
      this.mainForm = null;
      this.mainButton = null;
      this.variants = [];
      this.isPastMainButton = false;
      this.isAtNaturalPosition = false;
      this.rafPending = false;

      this.onViewportChange = this.onViewportChange.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
    }

    connectedCallback() {
      this.loadVariants();
      this.findMainProductForm();

      if (!this.mainButton || !this.sectionRoot) return;

      window.addEventListener('scroll', this.onViewportChange, { passive: true });
      window.addEventListener('resize', this.onViewportChange, { passive: true });

      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
        this.unsubscribeVariant = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.onVariantChange
        );
      }

      this.syncFromMainForm();
      this.updateLayout();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onViewportChange);
      window.removeEventListener('resize', this.onViewportChange);

      if (typeof this.unsubscribeVariant === 'function') {
        this.unsubscribeVariant();
      }
    }

    loadVariants() {
      const script = this.sectionRoot?.querySelector('[data-sticky-variants-json]');
      if (!script) return;

      try {
        this.variants = JSON.parse(script.textContent || '[]');
      } catch (error) {
        this.variants = [];
      }
    }

    findMainProductForm() {
      const productId = this.dataset.productId;

      const candidateForms = Array.from(
        document.querySelectorAll('product-info product-form form, .product product-form form')
      );

      this.mainForm = candidateForms.find((form) => {
        if (form.id === 'product-form-sticky-atc-bar') return false;

        const productIdInput = form.querySelector('[name="product-id"]');
        return !productIdInput || productIdInput.value === productId;
      }) || document.querySelector(
        'product-info product-form form:not(#product-form-sticky-atc-bar)'
      );

      this.mainButton =
        this.mainForm?.querySelector('[type="submit"]') || null;
    }

    onViewportChange() {
      if (this.rafPending) return;

      this.rafPending = true;

      requestAnimationFrame(() => {
        this.rafPending = false;
        this.updateLayout();
      });
    }

    updateLayout() {
      if (!this.mainButton || !this.sectionRoot) return;

      const mainButtonRect =
        this.mainButton.getBoundingClientRect();

      const barHeight =
        Math.ceil(this.getBoundingClientRect().height || 0);

      const sectionRect =
        this.sectionRoot.getBoundingClientRect();

      this.isPastMainButton =
        mainButtonRect.bottom <= 0;

      /*
       * The section lives AFTER the complete footer.
       * While that natural location is still below
       * the viewport, the bar stays fixed.
       *
       * Once the page-end slot reaches the viewport,
       * the bar returns to normal document flow and
       * becomes the last block on the page.
       */
      this.isAtNaturalPosition =
        sectionRect.top <=
        window.innerHeight - Math.max(barHeight, 1);

      const shouldShow =
        this.isPastMainButton;

      const shouldFix =
        shouldShow && !this.isAtNaturalPosition;

      this.classList.toggle(
        'is-visible',
        shouldShow
      );

      this.classList.toggle(
        'is-fixed',
        shouldFix
      );

      this.setAttribute(
        'aria-hidden',
        shouldShow ? 'false' : 'true'
      );

      this.sectionRoot.style.setProperty(
        '--norive-sticky-height',
        shouldShow ? `${barHeight}px` : '0px'
      );
    }

    onVariantChange(event) {
      const variant = event?.data?.variant;

      if (!variant) return;

      this.syncVariant(
        variant.id,
        variant
      );
    }

    syncFromMainForm() {
      const mainVariantInput =
        this.mainForm?.querySelector('[name="id"]');

      const variantId =
        Number(mainVariantInput?.value || 0);

      if (!variantId) return;

      this.syncVariant(variantId);
    }

    syncVariant(variantId, eventVariant = null) {
      const localVariant =
        this.variants.find(
          (variant) =>
            Number(variant.id) === Number(variantId)
        );

      const variant =
        localVariant || eventVariant;

      if (!variant) return;

      if (this.variantSelect) {
        this.variantSelect.value =
          String(variantId);
      }

      const variantTitle =
        variant.title ||
        eventVariant?.title ||
        '';

      const isDefault =
        variantTitle === 'Default Title';

      if (this.variantTitle) {
        this.variantTitle.textContent =
          isDefault ? '' : variantTitle;

        this.variantTitle.hidden =
          isDefault || !variantTitle;
      }

      const available =
        typeof variant.available === 'boolean'
          ? variant.available
          : Boolean(eventVariant?.available);

      this.updateButton(available);
      this.updatePrice(variant, eventVariant);
      this.updateImage(variant, eventVariant);
    }

    updateButton(available) {
      if (!this.submitButton) return;

      this.submitButton.setAttribute(
        'aria-disabled',
        available ? 'false' : 'true'
      );

      this.submitButton.toggleAttribute(
        'disabled',
        !available
      );

      if (this.buttonText) {
        this.buttonText.textContent =
          available
            ? window.variantStrings?.addToCart || 'Add to cart'
            : window.variantStrings?.soldOut || 'Sold out';
      }
    }

    updatePrice(localVariant, eventVariant) {
      if (
        !this.priceWrap ||
        !this.currentPrice
      ) {
        return;
      }

      if (localVariant?.price) {
        this.currentPrice.textContent =
          localVariant.price;

        let compare =
          this.priceWrap.querySelector(
            '.norive-sticky-atc-bar__compare-price'
          );

        if (
          localVariant.compareAtPrice &&
          localVariant.compareAtPrice !== localVariant.price
        ) {
          if (!compare) {
            compare =
              document.createElement('s');

            compare.className =
              'norive-sticky-atc-bar__compare-price';

            this.priceWrap.prepend(compare);
          }

          compare.textContent =
            localVariant.compareAtPrice;
        } else if (compare) {
          compare.remove();
        }

        return;
      }

      /*
       * Fallback:
       * copy the price Dawn just rendered
       * in the main product.
       */
      requestAnimationFrame(() => {
        const productInfo =
          document.querySelector('product-info');

        const sectionId =
          productInfo?.dataset.section;

        const priceSource =
          sectionId
            ? document.getElementById(
                `price-${sectionId}`
              )
            : null;

        const visiblePrice =
          priceSource?.querySelector(
            '.price-item--regular, .price-item--sale'
          );

        if (visiblePrice) {
          this.currentPrice.textContent =
            visiblePrice.textContent.trim();
        }
      });
    }

    updateImage(localVariant, eventVariant) {
      if (!this.image) return;

      let src =
        localVariant?.image || null;

      if (!src) {
        const media =
          eventVariant?.featured_media ||
          eventVariant?.featured_image;

        const preview =
          media?.preview_image || media;

        src =
          preview?.src || null;
      }

      if (!src) return;

      if (src.startsWith('//')) {
        src = `https:${src}`;
      }

      this.image.src = src;
      this.image.removeAttribute('srcset');
    }
  }

  customElements.define(
    'norive-sticky-atc-bar',
    NoriveStickyAtcBar
  );

  const upgradeBars = () => {
    document
      .querySelectorAll(
        '[data-norive-sticky-atc]'
      )
      .forEach((bar) => {
        if (
          bar.tagName.toLowerCase() ===
          'norive-sticky-atc-bar'
        ) {
          return;
        }

        const replacement =
          document.createElement(
            'norive-sticky-atc-bar'
          );

        Array.from(bar.attributes).forEach(
          (attr) => {
            replacement.setAttribute(
              attr.name,
              attr.value
            );
          }
        );

        replacement.innerHTML =
          bar.innerHTML;

        bar.replaceWith(replacement);
      });
  };

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      upgradeBars
    );
  } else {
    upgradeBars();
  }

  document.addEventListener(
    'shopify:section:load',
    upgradeBars
  );
})();